import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import PersonIcon from "@material-ui/icons/Person";
import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import { Can } from "../../components/Can";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  getStoredCompanyTimezone,
  formatCompanyDateTime,
  resolveCompanyTimezone
} from "../../utils/companyTimezone";
import { getTicketDisplayNumber } from "../../utils/ticketNumber";

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    minHeight: "100%",
    overflowX: "hidden"
  },
  dashboardPageWrapper: {
    padding: 10,
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0
  },
  mainPaper: {
    flex: 1,
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    boxSizing: "border-box",
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: "transparent !important",
    boxShadow: "none",
    ...theme.scrollbarStyles
  },
  page: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minHeight: 0
  },
  searchBox: {
    padding: theme.spacing(2),
    borderRadius: 16
  },
  resultsBox: {
    padding: theme.spacing(2),
    borderRadius: 16,
    minHeight: 420
  },
  searchInput: {
    marginTop: theme.spacing(1)
  },
  tabs: {
    marginBottom: theme.spacing(2)
  },
  resultCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
    cursor: "pointer",
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: theme.shadows[4],
      borderColor: theme.palette.primary.main
    }
  },
  resultTitle: {
    fontWeight: 700
  },
  resultMeta: {
    color: theme.palette.text.secondary
  },
  excerpt: {
    marginTop: theme.spacing(1),
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2)
  },
  emptyState: {
    minHeight: 260,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  clickHint: {
    marginTop: theme.spacing(1.25),
    color: theme.palette.primary.main,
    fontWeight: 600
  }
}));



const emptySection = { items: [], count: 0, hasMore: false, pageNumber: 1, limit: 20 };

const normalizeStandardizedResult = (data, currentTab) => {
  const activeType = currentTab === "all" ? "all" : currentTab;
  const globalPageNumber = Number(data?.pageNumber) || 1;
  const globalLimit = Number(data?.limit) || 10;
  const counts = data?.counts || {};
  const meta = data?.meta || {};

  const contactsItems = (data?.contacts || []).map(item => ({
    id: item.id,
    name: item.name || item.title,
    number: item.number || item.subtitle,
    companyName: item.companyName || item.snippet?.replace(/^Empresa:\s*/i, "") || "",
    profilePicUrl: item.profilePicUrl || null,
    ticketsCount: item.ticketsCount || 0,
    lastInteraction: item.lastInteraction || item.createdAt,
    lastTicketId: item.ticketId || null,
    lastTicketNumber: item.ticketNumber || null,
    lastTicketUuid: item.ticketUuid || null,
  }));

  const messageItems = (data?.messages || []).map(item => ({
    id: item.id,
    body: item.snippet,
    createdAt: item.createdAt,
    ticketId: item.ticketId || null,
    ticketNumber: item.ticketNumber || null,
    ticketUuid: item.ticketUuid || null,
    contactId: item.contactId || null,
    contactName: item.title,
    contactNumber: item.subtitle,
    ticketStatus: "",
  }));

  const ticketItems = (data?.tickets || []).map(item => ({
    id: item.id,
    ticketNumber: item.ticketNumber || null,
    uuid: item.ticketUuid || null,
    status: item.status || "",
    lastMessage: item.lastMessage || item.snippet,
    updatedAt: item.createdAt,
    contactId: item.contactId || null,
    contactName: item.contactName || item.subtitle || item.title?.replace(/^Ticket\s+#\d+\s*-\s*/i, "") || item.title,
    contactNumber: item.contactNumber || item.subtitle,
    queueName: item.queueName || "",
    userName: item.userName || "",
  }));

  const contactsMeta = meta.contacts || {};
  const messagesMeta = meta.messages || {};
  const ticketsMeta = meta.tickets || {};

  const contactsCount = Number(counts.contacts);
  const messagesCount = Number(counts.messages);
  const ticketsCount = Number(counts.tickets);

  return {
    contacts: {
      items: contactsItems,
      count: Number.isFinite(contactsCount) ? contactsCount : contactsItems.length,
      hasMore: Boolean(contactsMeta.hasMore),
      pageNumber: Number(contactsMeta.pageNumber) || (activeType === "contacts" ? globalPageNumber : 1),
      limit: Number(contactsMeta.limit) || globalLimit,
    },
    messages: {
      items: messageItems,
      count: Number.isFinite(messagesCount) ? messagesCount : messageItems.length,
      hasMore: Boolean(messagesMeta.hasMore),
      pageNumber: Number(messagesMeta.pageNumber) || (activeType === "messages" ? globalPageNumber : 1),
      limit: Number(messagesMeta.limit) || globalLimit,
    },
    tickets: {
      items: ticketItems,
      count: Number.isFinite(ticketsCount) ? ticketsCount : ticketItems.length,
      hasMore: Boolean(ticketsMeta.hasMore),
      pageNumber: Number(ticketsMeta.pageNumber) || (activeType === "tickets" ? globalPageNumber : 1),
      limit: Number(ticketsMeta.limit) || globalLimit,
    },
  };
};

const GlobalSearch = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const companyTimezone = resolveCompanyTimezone(user?.company?.timezone || getStoredCompanyTimezone());

  console.log(`[TZ_GLOBAL_SEARCH] GlobalSearch render: companyTimezone=${companyTimezone}`);

  const formatDate = value => {
    if (!value) return "--";
    return formatCompanyDateTime(value, companyTimezone);
  };

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState({
    contacts: 1,
    messages: 1,
    tickets: 1
  });
  const [result, setResult] = useState({
    contacts: emptySection,
    messages: emptySection,
    tickets: emptySection
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);

    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    setPages({
      contacts: 1,
      messages: 1,
      tickets: 1
    });
  }, [debouncedSearch]);

  useEffect(() => {
    const fetchSearch = async () => {
      if (debouncedSearch.length < 2) {
        setResult({
          contacts: emptySection,
          messages: emptySection,
          tickets: emptySection
        });
        return;
      }

      setLoading(true);
      try {
        const endpointType = tab === "all" ? "all" : tab;
        const activePage = tab === "all" ? 1 : pages[tab];
        const { data } = await api.get("/global-search", {
          params: {
            q: debouncedSearch,
            type: endpointType,
            pageNumber: activePage,
            limit: 10
          }
        });

        setResult(normalizeStandardizedResult(data, tab));
      } catch (error) {
        toastError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchSearch();
  }, [debouncedSearch, pages, tab]);

  const activeSection = result[tab] || { items: [], count: 0, hasMore: false, pageNumber: 1, limit: 10 };
  const allItems = useMemo(() => {
    const contacts = (result.contacts?.items || []).map(item => ({ ...item, __type: "contacts" }));
    const messages = (result.messages?.items || []).map(item => ({ ...item, __type: "messages" }));
    const tickets = (result.tickets?.items || []).map(item => ({ ...item, __type: "tickets" }));

    return [...messages, ...tickets, ...contacts].sort((left, right) => {
      const leftDate = new Date(left.createdAt || left.updatedAt || left.lastInteraction || 0).getTime();
      const rightDate = new Date(right.createdAt || right.updatedAt || right.lastInteraction || 0).getTime();
      return rightDate - leftDate;
    });
  }, [result]);

  const allCount = useMemo(
    () => safeCount(result.contacts?.count) + safeCount(result.messages?.count) + safeCount(result.tickets?.count),
    [result]
  );

  function safeCount(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const placeholder = useMemo(() => {
    if (tab === "all") return i18n.t("globalSearch.placeholders.all");
    if (tab === "messages") return i18n.t("globalSearch.placeholders.messages");
    if (tab === "tickets") return i18n.t("globalSearch.placeholders.tickets");
    return i18n.t("globalSearch.placeholders.contacts");
  }, [tab]);

  const goToTicket = (ticketUuid, messageId) => {
    if (!ticketUuid) return;
    const suffix = messageId ? `?focusedMessageId=${messageId}` : "";
    history.push(`/tickets/${ticketUuid}${suffix}`);
  };

  const goToContact = contact => {
    const searchTerm = contact?.number || contact?.name || "";
    history.push(`/contacts?searchParam=${encodeURIComponent(searchTerm)}`);
  };

  const handleCardKeyDown = (event, callback) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      callback();
    }
  };

  const updateCurrentTabPage = nextPage => {
    if (tab === "all") return;
    setPages(prev => ({
      ...prev,
      [tab]: Math.max(1, nextPage)
    }));
  };

  const renderContacts = () =>
    activeSection.items.map(contact => (
      <Grid item xs={12} key={`contact-${contact.id}`}>
        <Paper
          className={classes.resultCard}
          elevation={0}
          onClick={() => (contact.lastTicketUuid ? goToTicket(contact.lastTicketUuid) : goToContact(contact))}
          onKeyDown={event =>
            handleCardKeyDown(event, () =>
              contact.lastTicketUuid ? goToTicket(contact.lastTicketUuid) : goToContact(contact)
            )
          }
          role="button"
          tabIndex={0}
        >
          <Box display="flex" alignItems="center" gridGap={16}>
            <Avatar src={contact.profilePicUrl || undefined}>
              <PersonIcon />
            </Avatar>
            <Box flex={1}>
              <Typography className={classes.resultTitle}>{contact.name || i18n.t("globalSearch.fallbacks.noName")}</Typography>
              <Typography className={classes.resultMeta}>
                {contact.number} | {i18n.t("globalSearch.meta.company")}: {contact.companyName} | {i18n.t("globalSearch.meta.tickets")}: {contact.ticketsCount}
              </Typography>
              <Typography className={classes.resultMeta}>
                {i18n.t("globalSearch.meta.lastInteraction")}: {formatDate(contact.lastInteraction)}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" className={classes.clickHint}>
            {contact.lastTicketUuid ? i18n.t("globalSearch.hints.openConversation") : i18n.t("globalSearch.hints.openContact")}
          </Typography>
        </Paper>
      </Grid>
    ));

  const renderMessages = () =>
    activeSection.items.map(message => (
      <Grid item xs={12} key={`message-${message.id}`}>
        <Paper
          className={classes.resultCard}
          elevation={0}
          onClick={() => goToTicket(message.ticketUuid, message.id)}
          onKeyDown={event => handleCardKeyDown(event, () => goToTicket(message.ticketUuid, message.id))}
          role="button"
          tabIndex={0}
        >
          <Typography className={classes.resultTitle}>{message.contactName || i18n.t("globalSearch.fallbacks.contact")}</Typography>
          <Typography className={classes.resultMeta}>
            {message.contactNumber} | {formatDate(message.createdAt)} | {i18n.t("globalSearch.meta.status")}: {message.ticketStatus}
          </Typography>
          <Typography className={classes.excerpt}>{message.body || i18n.t("globalSearch.fallbacks.noTextMessage")}</Typography>
          <Typography variant="body2" className={classes.clickHint}>
            {i18n.t("globalSearch.hints.openMessage")}
          </Typography>
        </Paper>
      </Grid>
    ));

  const renderTickets = () =>
    activeSection.items.map(ticket => (
      <Grid item xs={12} key={`ticket-${ticket.id}`}>
        <Paper
          className={classes.resultCard}
          elevation={0}
          onClick={() => goToTicket(ticket.uuid)}
          onKeyDown={event => handleCardKeyDown(event, () => goToTicket(ticket.uuid))}
          role="button"
          tabIndex={0}
        >
          <Typography className={classes.resultTitle}>
            Ticket #{getTicketDisplayNumber(ticket)} - {ticket.contactName || i18n.t("globalSearch.fallbacks.noContact")}
          </Typography>
          <Typography className={classes.resultMeta}>
            {ticket.contactNumber} | {i18n.t("globalSearch.meta.status")}: {ticket.status} | {i18n.t("globalSearch.meta.queue")}: {ticket.queueName || i18n.t("globalSearch.fallbacks.noQueue")} | {i18n.t("globalSearch.meta.responsible")}: {ticket.userName || i18n.t("globalSearch.fallbacks.notAssigned")}
          </Typography>
          <Typography className={classes.excerpt}>{ticket.lastMessage || i18n.t("globalSearch.fallbacks.noLastMessage")}</Typography>
          <Typography variant="body2" className={classes.clickHint}>
            {i18n.t("globalSearch.hints.openTicket")}
          </Typography>
        </Paper>
      </Grid>
    ));

  const renderActiveTab = () => {
    if (loading) {
      return (
        <div className={classes.emptyState}>
          <Box>
            <CircularProgress size={28} />
            <Typography variant="body2">{i18n.t("globalSearch.loading")}</Typography>
          </Box>
        </div>
      );
    }

    if (debouncedSearch.length < 2) {
      return (
        <div className={classes.emptyState}>
          <Box>
            <SearchIcon style={{ fontSize: 42, opacity: 0.6 }} />
            <Typography variant="h6">{i18n.t("globalSearch.empty.title")}</Typography>
            <Typography variant="body2">{i18n.t("globalSearch.empty.minChars")}</Typography>
          </Box>
        </div>
      );
    }

    if (tab === "all") {
      if (!allItems.length) {
        return (
          <div className={classes.emptyState}>
            <Typography variant="body2">{i18n.t("globalSearch.empty.noGlobalResults")}</Typography>
          </div>
        );
      }

      return (
        <>
          <Grid container spacing={2}>
            {allItems.map(item => {
              if (item.__type === "contacts") {
                return (
                  <Grid item xs={12} key={`contact-${item.id}`}>
                    <Paper
                      className={classes.resultCard}
                      elevation={0}
                      onClick={() => (item.lastTicketUuid ? goToTicket(item.lastTicketUuid) : goToContact(item))}
                      onKeyDown={event =>
                        handleCardKeyDown(event, () =>
                          item.lastTicketUuid ? goToTicket(item.lastTicketUuid) : goToContact(item)
                        )
                      }
                      role="button"
                      tabIndex={0}
                    >
                      <Box display="flex" alignItems="center" gridGap={16}>
                        <Avatar src={item.profilePicUrl || undefined}>
                          <PersonIcon />
                        </Avatar>
                        <Box flex={1}>
                          <Typography className={classes.resultTitle}>[{i18n.t("globalSearch.types.contact")}] {item.name || i18n.t("globalSearch.fallbacks.noName")}</Typography>
                          <Typography className={classes.resultMeta}>
                            {item.number} | {i18n.t("globalSearch.meta.company")}: {item.companyName} | {i18n.t("globalSearch.meta.tickets")}: {item.ticketsCount}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" className={classes.clickHint}>
                        {item.lastTicketUuid ? i18n.t("globalSearch.hints.openConversation") : i18n.t("globalSearch.hints.openContact")}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              }

              if (item.__type === "messages") {
                return (
                  <Grid item xs={12} key={`message-${item.id}`}>
                    <Paper
                      className={classes.resultCard}
                      elevation={0}
                      onClick={() => goToTicket(item.ticketUuid, item.id)}
                      onKeyDown={event => handleCardKeyDown(event, () => goToTicket(item.ticketUuid, item.id))}
                      role="button"
                      tabIndex={0}
                    >
                      <Typography className={classes.resultTitle}>[{i18n.t("globalSearch.types.message")}] {item.contactName || i18n.t("globalSearch.fallbacks.contact")}</Typography>
                      <Typography className={classes.resultMeta}>
                        {item.contactNumber} | {formatDate(item.createdAt)} | {i18n.t("globalSearch.meta.status")}: {item.ticketStatus}
                      </Typography>
                      <Typography className={classes.excerpt}>{item.body || i18n.t("globalSearch.fallbacks.noTextMessage")}</Typography>
                      <Typography variant="body2" className={classes.clickHint}>
                        {i18n.t("globalSearch.hints.openMessage")}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} key={`ticket-${item.id}`}>
                  <Paper
                    className={classes.resultCard}
                    elevation={0}
                    onClick={() => goToTicket(item.uuid)}
                    onKeyDown={event => handleCardKeyDown(event, () => goToTicket(item.uuid))}
                    role="button"
                    tabIndex={0}
                  >
                    <Typography className={classes.resultTitle}>
                      [{i18n.t("globalSearch.types.ticket")}] #{getTicketDisplayNumber(item)} - {item.contactName || i18n.t("globalSearch.fallbacks.noContact")}
                    </Typography>
                    <Typography className={classes.resultMeta}>
                      {item.contactNumber} | {i18n.t("globalSearch.meta.status")}: {item.status} | {i18n.t("globalSearch.meta.queue")}: {item.queueName || i18n.t("globalSearch.fallbacks.noQueue")} | {i18n.t("globalSearch.meta.responsible")}: {item.userName || i18n.t("globalSearch.fallbacks.notAssigned")}
                    </Typography>
                    <Typography className={classes.excerpt}>{item.lastMessage || i18n.t("globalSearch.fallbacks.noLastMessage")}</Typography>
                    <Typography variant="body2" className={classes.clickHint}>
                      {i18n.t("globalSearch.hints.openTicket")}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
          <div className={classes.pagination}>
            <Typography variant="body2">
              {i18n.t("globalSearch.results.combined", { count: allCount })}
            </Typography>
          </div>
        </>
      );
    }

    if (!activeSection.items.length) {
      return (
        <div className={classes.emptyState}>
          <Typography variant="body2">{i18n.t("globalSearch.empty.noTabResults")}</Typography>
        </div>
      );
    }

    return (
      <>
        <Grid container spacing={2}>
          {tab === "contacts" && renderContacts()}
          {tab === "messages" && renderMessages()}
          {tab === "tickets" && renderTickets()}
        </Grid>
        <div className={classes.pagination}>
          <Button
            onClick={() => updateCurrentTabPage(activeSection.pageNumber - 1)}
            disabled={activeSection.pageNumber <= 1 || loading}
          >
            {i18n.t("common.buttons.previous")}
          </Button>
          <Typography variant="body2">
            {i18n.t("globalSearch.pagination.page")} {activeSection.pageNumber} | {i18n.t("globalSearch.pagination.results", { count: activeSection.count })}
          </Typography>
          <Button
            onClick={() => updateCurrentTabPage(activeSection.pageNumber + 1)}
            disabled={!activeSection.hasMore || loading}
            color="primary"
          >
            {i18n.t("common.buttons.next")}
          </Button>
        </div>
      </>
    );
  };

  return (
    <Can
      role={user.profile}
      perform="search:view"
      yes={() => (
        <MainContainer style={{ padding: 0 }} className={classes.pageRoot}>
          <div className={classes.dashboardPageWrapper}>
            <Paper className={classes.mainPaper} variant="outlined">
              <div className={classes.page}>
            <Paper className={classes.searchBox} elevation={0}>
              <Typography variant="h5">{i18n.t("globalSearch.title")}</Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("globalSearch.subtitle")}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={placeholder}
                className={classes.searchInput}
                value={search}
                onChange={event => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon color="action" style={{ marginRight: 8 }} />
                }}
              />
            </Paper>

            <Paper className={classes.resultsBox} elevation={0}>
              <Tabs
                value={tab}
                onChange={(_event, value) => setTab(value)}
                indicatorColor="primary"
                textColor="primary"
                className={classes.tabs}
              >
                <Tab value="all" label={`${i18n.t("globalSearch.tabs.all")} (${allCount})`} />
                <Tab value="contacts" label={`${i18n.t("globalSearch.tabs.contacts")} (${result.contacts.count || 0})`} />
                <Tab value="messages" label={`${i18n.t("globalSearch.tabs.messages")} (${result.messages.count || 0})`} />
                <Tab value="tickets" label={`${i18n.t("globalSearch.tabs.tickets")} (${result.tickets.count || 0})`} />
              </Tabs>
              <Divider />
              <Box mt={2}>{renderActiveTab()}</Box>
            </Paper>
              </div>
            </Paper>
          </div>
        </MainContainer>
      )}
      no={() => <ForbiddenPage />}
    />
  );
};

export default GlobalSearch;
